/**
 * IZGLED — who you are, as one integer.
 *
 * This file used to hold eight ready-made characters and nothing else, because onboarding is
 * allowed exactly one real question (GDD §4) and "which of these four faces" is not it. That is
 * still true and the picker is still a row of portraits you tap. What changed is what happens
 * *after* onboarding: there is now an editor, and nine axes behind it.
 *
 * ## Why it is still one integer
 *
 * `look` crosses five boundaries — the `members.look` column, `/api/state`, the room URL the
 * Worker signs, `Present` on the live wire, and the key every sprite is baked under. A rich object
 * would have to be threaded through all five, which means a protocol version bump, which means
 * everybody standing on an island gets disconnected the moment this ships. A packed integer changes
 * none of them, needs no migration, and has one property no side-channel has: **an appearance is
 * one value**, so a cache key that already includes `look` is automatically correct.
 *
 * ## Why nobody's face changes
 *
 * Values 0-7 are left exactly where they were. They are not packed — they are eight shorthands,
 * decoded through `LEGACY` into the same characters they have always drawn. Every member who chose
 * before this existed opens the app as precisely who they were, and the column keeps the INTEGER it
 * has had since 0001. That is the same argument this file already made about appending the second
 * body rather than interleaving it, applied one level up.
 *
 * ## Adding to it later
 *
 * Two rules, and they are the whole contract:
 *
 *   1. **A slot's radix is frozen forever.** Adding an eleventh hairstyle is free because the slot
 *      holds sixteen. Widening the slot is not: every packed value would decode differently, and
 *      several hundred people would find somebody else's face on their character.
 *   2. **A new axis is appended at the high end**, never inserted. Existing values then decode with
 *      the new field at 0, which is why every axis's option 0 has to be its plainest one.
 */

/* ------------------------------------------------------------------ the axes */

/**
 * The nine things you can change about yourself.
 *
 * Three of them are colours and six are shapes, which is deliberate: at sixteen pixels a colour is
 * the only change that is legible at map scale, and a shape is the only change that is legible at
 * portrait scale. An editor with only one kind would feel like it did nothing on one of the two
 * screens the character actually appears on.
 */
export interface Izgled {
  /** Skin tone. Also shades the hands, the neck and any bare arms the outfit leaves. */
  skin: number;
  /** Hairstyle. Option 0 is the short crop every character had before this file grew. */
  hair: number;
  hairColor: number;
  /** Eye colour. Option 0 is black, because that is what the eyes were when they were outline. */
  eyes: number;
  /** Facial hair. Drawn in its own key, a shade off the hair — see `characters.ts` on why. */
  beard: number;
  outfit: number;
  outfitColor: number;
  trousers: number;
  /** Glasses, an earring, a headband. One at a time; a second slot is a second thing to learn. */
  extra: number;
}

export type IzgledAxis = keyof Izgled;

/**
 * How many options each axis offers today.
 *
 * Declared here rather than derived from the art, because `core` is shared with the Worker and the
 * Worker has no art — it needs to fold a hostile number onto something drawable without importing
 * a single pixel. `tests/izgled.test.ts` asserts these against the actual arrays in
 * `ui/art/features.ts`, so the two cannot drift apart in silence.
 */
export const OPTIONS: Readonly<Record<IzgledAxis, number>> = {
  skin: 8,
  hair: 10,
  hairColor: 12,
  eyes: 6,
  beard: 6,
  outfit: 7,
  outfitColor: 12,
  trousers: 8,
  extra: 5,
};

/**
 * The packing layout. **Neither the order nor any radix may ever change** — see rule 1 above.
 *
 * Radices are powers of two only because they are easy to reason about, not because anything here
 * is a bit operation: JavaScript's bitwise operators are 32-bit and signed, and the whole layout is
 * wider than that, so packing is done in ordinary arithmetic. Colours get thirty-two because a
 * palette is the thing most likely to grow; shapes get sixteen because each one is hand-drawn art
 * and sixteen of anything is already a long afternoon.
 */
const SLOTS: ReadonlyArray<readonly [IzgledAxis, number]> = [
  ['skin', 16],
  ['hair', 16],
  ['hairColor', 32],
  ['eyes', 16],
  ['beard', 16],
  ['outfit', 16],
  ['outfitColor', 32],
  ['trousers', 16],
  ['extra', 16],
];

/** Largest packed value the layout can produce, exclusive. Checked against MAX_SAFE_INTEGER. */
export const LOOK_SPACE = SLOTS.reduce((total, [, radix]) => total * radix, 1);

/* ------------------------------------------------------------------ the eight that came first */

/**
 * The four colour combos the game shipped with, and the two heads that wore them.
 *
 * `look % 4` was the combo and `look < 4` was the body. Both survive here as data rather than as
 * arithmetic, so the rule that used to be spread across three files is now one table that a test
 * can read.
 */
const LEGACY_COMBOS = 4;
export const LEGACY_COUNT = LEGACY_COMBOS * 2;

/** Hairstyle 0 is the old short crop; hairstyle 1 is the old long hair. Both are art, now. */
const KRATKA = 0;
const DUGA = 1;

export const LEGACY: readonly Izgled[] = Array.from({ length: LEGACY_COUNT }, (_, index) => {
  const combo = index % LEGACY_COMBOS;
  return {
    skin: combo,
    hair: index < LEGACY_COMBOS ? KRATKA : DUGA,
    hairColor: combo,
    eyes: 0,
    beard: 0,
    outfit: 0,
    outfitColor: combo,
    trousers: 0,
    extra: 0,
  };
});

/**
 * Where packed values start.
 *
 * Everything below it is one of the eight above. It costs eight integers out of two hundred and
 * seventy billion and it is why this change needs no migration at all.
 */
export const PACKED_BASE = LEGACY_COUNT;

/* ------------------------------------------------------------------ folding */

const clampAxis = (value: unknown, count: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(count - 1, Math.trunc(value)));
};

/**
 * Fold anything at all onto an appearance that exists.
 *
 * Clamping rather than rejecting is the posture this file has always taken, and the reason has not
 * changed: an appearance is decoration, and somebody whose row is corrupt should end up looking
 * like a person rather than getting an error where their character was. It now also covers a case
 * that did not exist before — a client running a newer bundle than the server, sending an axis
 * value for art this Worker has never heard of. That folds to the plainest option on that axis and
 * everything else about them still arrives intact.
 */
export function normaliseIzgled(value: Partial<Izgled> | null | undefined): Izgled {
  const raw = value ?? {};
  return {
    skin: clampAxis(raw.skin, OPTIONS.skin),
    hair: clampAxis(raw.hair, OPTIONS.hair),
    hairColor: clampAxis(raw.hairColor, OPTIONS.hairColor),
    eyes: clampAxis(raw.eyes, OPTIONS.eyes),
    beard: clampAxis(raw.beard, OPTIONS.beard),
    outfit: clampAxis(raw.outfit, OPTIONS.outfit),
    outfitColor: clampAxis(raw.outfitColor, OPTIONS.outfitColor),
    trousers: clampAxis(raw.trousers, OPTIONS.trousers),
    extra: clampAxis(raw.extra, OPTIONS.extra),
  };
}

export const sameIzgled = (a: Izgled, b: Izgled): boolean =>
  (Object.keys(OPTIONS) as IzgledAxis[]).every((axis) => a[axis] === b[axis]);

/* ------------------------------------------------------------------ the codec */

/**
 * An appearance, as the integer that goes on the wire.
 *
 * The eight shorthands win when they apply, which is what keeps a round trip stable: somebody who
 * taps the first preset and saves gets `0` back, the same value they would have got a year ago,
 * rather than a packed number that draws the identical character. Two integers meaning one
 * appearance is harmless — every consumer keys on the value and the sprite is the same either way —
 * but only one of them should ever be *written*.
 */
export function encodeLook(value: Partial<Izgled>): number {
  const izgled = normaliseIzgled(value);
  const legacy = LEGACY.findIndex((preset) => sameIzgled(preset, izgled));
  if (legacy >= 0) return legacy;

  let packed = 0;
  let multiplier = 1;
  for (const [axis, radix] of SLOTS) {
    packed += izgled[axis] * multiplier;
    multiplier *= radix;
  }
  return PACKED_BASE + packed;
}

/** The stored integer, unpacked into the person it stands for. Total: nothing throws, ever. */
export function decodeLook(value: number): Izgled {
  if (!Number.isFinite(value)) return LEGACY[0]!;
  const raw = Math.trunc(value);
  if (raw < 0) return LEGACY[0]!;
  if (raw < PACKED_BASE) return LEGACY[raw]!;

  let rest = raw - PACKED_BASE;
  const out: Partial<Izgled> = {};
  for (const [axis, radix] of SLOTS) {
    out[axis] = rest % radix;
    rest = Math.floor(rest / radix);
  }
  // Anything left over is a value from beyond the layout — a future slot, or noise. Both are the
  // same thing to this version of the code, and both are already fully described by what was read.
  return normaliseIzgled(out);
}

/**
 * Fold any integer onto one that this build can draw.
 *
 * The server's only job on the way in: whatever arrives is decoded, clamped axis by axis and
 * re-encoded, so the number written to D1 is always one this code understands. Kept under its
 * original name because it has the same contract it always had, over a wider domain.
 */
export function clampLook(value: number): number {
  return encodeLook(decodeLook(value));
}

/* ------------------------------------------------------------------ starting points */

/**
 * The characters offered on the way in.
 *
 * Twelve, and every one of them is a different *person* rather than a different palette row — the
 * eight that came before were four heads recoloured, which read as four portraits of nobody. The
 * point of a starting point is that somebody sees one and thinks "that's me, near enough"; the
 * editor is for the rest of the distance.
 *
 * The old eight are not in here and do not need to be: they still decode, they still draw, and
 * anybody wearing one keeps it until they choose otherwise. This list is what a *new* member is
 * shown, and a preset row that has to carry a backwards-compatibility table is a worse preset row.
 */
export const PRESETS: readonly Izgled[] = [
  // short dark crop, cyan tee — the character the game has opened on since day one
  { skin: 1, hair: 0, hairColor: 0, eyes: 1, beard: 0, outfit: 0, outfitColor: 0, trousers: 0, extra: 0 },
  // long black hair, red tee
  { skin: 0, hair: 1, hairColor: 2, eyes: 0, beard: 0, outfit: 0, outfitColor: 1, trousers: 1, extra: 0 },
  // wavy auburn, striped
  { skin: 2, hair: 2, hairColor: 1, eyes: 5, beard: 0, outfit: 1, outfitColor: 6, trousers: 2, extra: 0 },
  // deep skin, coils, gold tee
  { skin: 6, hair: 5, hairColor: 0, eyes: 1, beard: 0, outfit: 0, outfitColor: 9, trousers: 0, extra: 0 },
  // blond ponytail, green hoodie
  { skin: 1, hair: 3, hairColor: 3, eyes: 2, beard: 0, outfit: 2, outfitColor: 3, trousers: 5, extra: 0 },
  // bun, glasses, buttoned shirt — the colourist
  { skin: 3, hair: 4, hairColor: 5, eyes: 1, beard: 0, outfit: 4, outfitColor: 5, trousers: 1, extra: 1 },
  // teal mohawk, black tank
  { skin: 2, hair: 7, hairColor: 8, eyes: 4, beard: 4, outfit: 5, outfitColor: 4, trousers: 1, extra: 0 },
  // ginger, full beard, glasses, navy jacket — warm hair against a cold jacket, because ginger on
  // orange on light skin is three warm values and reads as one smudge at map scale
  { skin: 4, hair: 0, hairColor: 6, eyes: 0, beard: 3, outfit: 3, outfitColor: 6, trousers: 2, extra: 1 },
  // braids, pink, headband
  { skin: 5, hair: 8, hairColor: 2, eyes: 3, beard: 0, outfit: 0, outfitColor: 10, trousers: 4, extra: 4 },
  // shaved head, goatee, graphic tee
  { skin: 3, hair: 9, hairColor: 2, eyes: 1, beard: 2, outfit: 6, outfitColor: 4, trousers: 0, extra: 3 },
  // platinum side part, cream shirt
  { skin: 0, hair: 6, hairColor: 4, eyes: 2, beard: 0, outfit: 4, outfitColor: 8, trousers: 6, extra: 0 },
  // silver long hair, purple, glasses
  { skin: 7, hair: 1, hairColor: 7, eyes: 4, beard: 0, outfit: 0, outfitColor: 2, trousers: 3, extra: 1 },
];

/** The presets as stored integers, in the order the picker draws them. */
export const PRESET_LOOKS: readonly number[] = PRESETS.map(encodeLook);

/**
 * A whole person at random.
 *
 * The editor has a dice button, and it earns its place: nine axes is enough that the first thing a
 * lot of people want is to be shown somewhere interesting rather than to make nine decisions. The
 * caller supplies the randomness so this stays pure and a test can pin it.
 */
export function randomIzgled(random: () => number = Math.random): Izgled {
  const pick = (count: number) => Math.min(count - 1, Math.floor(random() * count));
  return {
    skin: pick(OPTIONS.skin),
    hair: pick(OPTIONS.hair),
    hairColor: pick(OPTIONS.hairColor),
    eyes: pick(OPTIONS.eyes),
    beard: pick(OPTIONS.beard),
    outfit: pick(OPTIONS.outfit),
    outfitColor: pick(OPTIONS.outfitColor),
    trousers: pick(OPTIONS.trousers),
    extra: pick(OPTIONS.extra),
  };
}
